// INV34 (docs/build-briefs/INV34-cost-and-margin.md; DEC-291): the pure,
// DOM-free wording behind the Cost and margin screen (ui/src/costMargin.ts),
// kept apart so a plain node:test file can import it
// (test/ui-cost-margin-model.test.ts).

import type { CostMarginDrinkDto, CostMarginPartDto } from "./api.ts";
import { formatMoney } from "./reportFormat.ts";
import { bottleFigure, categoryName, decimalText } from "./usageWasteModel.ts";

export { categoryName };

/** A percent from basis points, "78.5%", or "-" when there is nothing to divide by. */
export function percentText(basisPoints: number | null): string {
  return basisPoints === null ? "-" : `${(basisPoints / 100).toFixed(1)}%`;
}

/** A drink's margin or pour cost percent. A costed drink with no sale (all of
 * it comped or refunded) has nothing to divide by, so it says so in words
 * instead of "-". A below-cost drink keeps its real negative percent. */
export function drinkPercentText(basisPoints: number | null): string {
  return basisPoints === null ? "n/a, no sale" : percentText(basisPoints);
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

export interface DrinkTag {
  text: string;
  tone: "out" | "low" | "info";
}

/** The tags under a drink's name, in the approved order. */
export function drinkTags(d: CostMarginDrinkDto): DrinkTag[] {
  const tags: DrinkTag[] = [];
  const costed = d.state === "ok" || d.state === "partial";
  if (costed && d.marginCents !== null && d.marginCents < 0) {
    tags.push({ text: "Below cost", tone: "out" });
  }
  if (!costed) {
    tags.push({ text: "No cost yet", tone: "low" });
  }
  if (d.state === "partial") {
    tags.push({ text: "Partly costed", tone: "low" });
  }
  if (d.remakes > 0) {
    tags.push({ text: `Remade ${d.remakes}`, tone: "info" });
  }
  if (costed && d.untrackedParts.length > 0) {
    tags.push({ text: "Part not tracked", tone: "info" });
  }
  if (d.priceChangedLines > 0) {
    tags.push({ text: `Price changed on ${d.priceChangedLines}`, tone: "info" });
  }
  return tags;
}

/** One part of a drink in words: "2 oz a drink, 4 oz in all, about 0.2 bottles". */
export function partLine(part: CostMarginPartDto): string {
  const unit = part.unit === "oz" ? "oz" : Number(part.quantity) === 1 ? "unit" : "units";
  const places = part.unit === "oz" ? 1 : 0;
  const perDrink =
    part.perDrink === null
      ? ""
      : `${decimalText(part.perDrink, Number(part.perDrink) % 1 === 0 ? 0 : 2)} ${unit} a drink, `;
  const bottles =
    part.containers === null
      ? ""
      : `, about ${bottleFigure(part.containers)} ${Number(part.containers) === 1 ? "bottle" : "bottles"}`;
  return `${perDrink}${decimalText(part.quantity, places)} ${unit} in all${bottles}`;
}

/** Why a drink is left out, for the list: names the parts with no price. */
export function whyLeftOut(d: CostMarginDrinkDto): { bold: string; rest: string } {
  if (d.state === "noparts") {
    return { bold: "No tracked parts.", rest: " Nothing is taken from stock when it is made." };
  }
  return { bold: "No cost yet", rest: ` on ${d.missingParts.join(", ")}.` };
}

/** The yellow notice, or null when every sale has a cost. */
export function leftOutNotice(left: {
  drinks: number;
  salesCents: number;
}): { bold: string; rest: string } | null {
  if (left.salesCents <= 0 && left.drinks === 0) {
    return null;
  }
  return {
    bold: `Cost, margin and pour cost leave out ${formatMoney(left.salesCents)} of sales. `,
    rest:
      `${left.drinks} ${plural(left.drinks, "drink has", "drinks have")} no part with a cost yet, or no tracked part. ` +
      "They are listed at the bottom. The Sales card still counts them, so it matches Sales by item.",
  };
}

export function partialNotice(drinks: number): { bold: string; rest: string } | null {
  if (drinks === 0) {
    return null;
  }
  return {
    bold: `${drinks} ${plural(drinks, "drink is", "drinks are")} partly costed. `,
    rest:
      "They are costed from the parts that have a price, so their cost is a little low until every part has one. " +
      "They are in margin and pour cost, and listed at the bottom.",
  };
}

/** The plain-words column of a drink's details. */
export function plainWords(d: CostMarginDrinkDto): string[] {
  if (d.state === "nocost" || d.state === "noparts") {
    return [
      `Its ${formatMoney(d.salesCents)} of sales is in the Sales card, and left out of cost, margin and pour cost.`,
    ];
  }
  const lines: string[] = [];
  const perSale = d.sold > 0 ? Math.round(d.salesCents / d.sold) : 0;
  lines.push(
    `Each drink sold for about ${formatMoney(perSale)} and cost ${formatMoney(d.costPerDrinkCents ?? 0)}. ` +
      (d.costedSalesCents > 0 && d.marginCents !== null
        ? `The location kept ${formatMoney(d.marginCents)}, or ${percentText(d.marginBp)} of sales.`
        : "Nothing was collected for it."),
  );
  if (d.marginCents !== null && d.marginCents < 0) {
    lines.push(
      d.compCents > 0
        ? "It sold below its cost. Comps took its sales away and the drinks were still made."
        : d.priceChangedLines > 0
          ? "It sold below its cost. Some lines were rung up at a changed price."
          : "It sold below its cost. Discounts took off more than the margin.",
    );
  }
  if (d.remakes > 0) {
    lines.push(
      `${d.remakes} ${plural(d.remakes, "remake", "remakes")} added ${formatMoney(d.remakeCostCents ?? 0)} of cost and no sales.`,
    );
  }
  if (d.refundCents > 0) {
    lines.push("Some were refunded. The drinks were already made, so their cost stays.");
  }
  if (d.state === "partial") {
    const parts = d.missingParts.length > 0 ? ` (${d.missingParts.join(", ")})` : "";
    lines.push(
      `Partly costed: it is costed from the parts that have a price. No price yet on${parts.length > 0 ? parts : " a part"}, ` +
        `so its cost is a little low.${d.leftOutSalesCents > 0 ? ` ${formatMoney(d.leftOutSalesCents)} of its sales had no priced part and is left out of margin.` : ""}`,
    );
  }
  if (d.untrackedParts.length > 0) {
    lines.push(`${d.untrackedParts.join(", ")} is not tracked, so it is not in the cost.`);
  }
  return lines;
}
